// FEAT-38 — the interaction fold. PURE: no DOM, no imports, no side effects
// (the ./displays.ts convention), so it unit-tests without jsdom and nothing
// in here can ever touch a listener.
//
// WHAT IT IS. docs/product/ai-agent-cheating-research.md §4 measured that an
// agent driving a browser or a desktop clicks WITHOUT the pointer travelling
// to the target (one warp, then a mousedown), produces a near-empty pointer
// stream, and pauses for its screenshot-reason-act loop — while a human hand
// produces hundreds of small moves and no such pattern. Those counts are
// cheap and deterministic, so the controller folds them per attempt and ships
// them as one `interaction_summary` event.
//
// WHAT IT IS NOT. The same counts describe Dragon MouseGrid, eye-gaze dwell
// clicking, switch access and head pointers, and a thinking human pauses too.
// So they are CONTEXT for the recruiter's attempt page, never a flag:
// backend/risk.py never reads them into RiskSignals and nothing in
// `_compute_risk_level` can see them (the CP-11 pattern).
//
// WHAT NEVER LEAVES THIS MODULE. The fold keeps only counters, the last
// timestamp, the last pointer position and a half-second ring of move
// distances. No key identity, no per-key timing, no coordinates and no raw
// events are ever retained or exposed — `counts()` is five integers.

export type InteractionInputType = "move" | "down" | "up" | "key" | "wheel";

export interface InteractionInput {
  type: InteractionInputType;
  /** Milliseconds on any monotonic-enough clock (`Date.now()` in the controller). */
  t: number;
  x?: number;
  y?: number;
}

export interface InteractionCounts {
  /** pointerdown events from a mouse. */
  clicks: number;
  /** Clicks with no pointer travel: zero moves in the previous
   *  TELEPORT_WINDOW_MS, or a single step over TELEPORT_JUMP_PX among them. */
  teleport_clicks: number;
  /** pointermove events from a mouse (raw count). */
  pointer_moves: number;
  /** Gaps of at least LONG_PAUSE_MS between any two inputs. */
  long_pauses: number;
  /** Milliseconds inside input runs — the sum of every gap shorter than LONG_PAUSE_MS. */
  active_ms: number;
}

/** The window before a click in which pointer travel counts as "arriving". */
export const TELEPORT_WINDOW_MS = 500;
/** A single move step longer than this is a warp, not travel. The spike's
 *  strict rule: a 99-px Playwright hop is travel, a 300-px XTest warp is not. */
export const TELEPORT_JUMP_PX = 200;
/** A gap between inputs this long or longer is a pause. */
export const LONG_PAUSE_MS = 2000;

export const ZERO_COUNTS: Readonly<InteractionCounts> = Object.freeze({
  clicks: 0,
  teleport_clicks: 0,
  pointer_moves: 0,
  long_pauses: 0,
  active_ms: 0,
});

const FIELDS: readonly (keyof InteractionCounts)[] = [
  "clicks",
  "teleport_clicks",
  "pointer_moves",
  "long_pauses",
  "active_ms",
];

function sane(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/** Copy `seed` into a fresh counts object; anything unreadable reads as 0. */
function sanitize(seed: Partial<InteractionCounts> | null | undefined): InteractionCounts {
  const out = { ...ZERO_COUNTS };
  if (seed && typeof seed === "object") {
    for (const f of FIELDS) out[f] = sane((seed as Record<string, unknown>)[f]);
  }
  return out;
}

/** Field-wise max — the server's merge (`backend/risk.py::interaction_context`)
 *  mirrored here, so a controller that adopts stored counts can never go
 *  backwards and a re-sent or reordered summary cannot double-count. */
export function maxCounts(
  a: Partial<InteractionCounts> | null | undefined,
  b: Partial<InteractionCounts> | null | undefined,
): InteractionCounts {
  const sa = sanitize(a);
  const sb = sanitize(b);
  const out = { ...ZERO_COUNTS };
  for (const f of FIELDS) out[f] = Math.max(sa[f], sb[f]);
  return out;
}

export interface InteractionFold {
  push(ev: InteractionInput): void;
  /** A copy of the cumulative counts — never the live state. */
  counts(): InteractionCounts;
}

/** The incremental fold. `seed` is what a previous controller of the same
 *  attempt had counted (the per-attempt sessionStorage copy); the fold
 *  continues from it. Bounded memory: a ring of moves from the last
 *  TELEPORT_WINDOW_MS (≈ 60 entries at 120 Hz), nothing else accumulates. */
export function createInteractionFold(
  seed?: Partial<InteractionCounts> | null,
): InteractionFold {
  const c = sanitize(seed);
  let lastT: number | null = null;
  let lastX: number | null = null;
  let lastY: number | null = null;
  // Moves inside the teleport window: when they happened and how far each
  // one stepped from the position before it. Coordinates are not kept.
  let recent: { t: number; step: number }[] = [];

  const prune = (now: number): void => {
    const cutoff = now - TELEPORT_WINDOW_MS;
    if (recent.length > 0 && recent[0].t <= cutoff) {
      recent = recent.filter((m) => m.t > cutoff);
    }
  };

  const hasPoint = (ev: InteractionInput): ev is InteractionInput & { x: number; y: number } =>
    typeof ev.x === "number" &&
    typeof ev.y === "number" &&
    Number.isFinite(ev.x) &&
    Number.isFinite(ev.y);

  const push = (ev: InteractionInput): void => {
    if (typeof ev.t !== "number" || !Number.isFinite(ev.t)) return;
    // Pause / active accounting on EVERY input, whatever its kind: a pause
    // is "nothing happened", not "the pointer stayed still".
    if (lastT !== null) {
      const gap = ev.t - lastT;
      if (gap >= LONG_PAUSE_MS) c.long_pauses += 1;
      else if (gap > 0) c.active_ms += gap;
      // A negative gap (a clock adjustment) is neither.
    }
    lastT = ev.t;

    if (ev.type === "move") {
      c.pointer_moves += 1;
      let step = 0;
      if (hasPoint(ev)) {
        // The first move has no previous position, so it never reads as a
        // jump: a miss here is a miss, never an accusation.
        if (lastX !== null && lastY !== null) {
          step = Math.hypot(ev.x - lastX, ev.y - lastY);
        }
        lastX = ev.x;
        lastY = ev.y;
      }
      prune(ev.t);
      recent.push({ t: ev.t, step });
      return;
    }

    if (ev.type === "down") {
      c.clicks += 1;
      prune(ev.t);
      const travelled = recent.length > 0 && recent.every((m) => m.step <= TELEPORT_JUMP_PX);
      if (!travelled) c.teleport_clicks += 1;
      if (hasPoint(ev)) {
        // The pointer IS here now; the next move steps from this point.
        lastX = ev.x;
        lastY = ev.y;
      }
      return;
    }
    // "up", "key", "wheel": timing only (already accounted above).
  };

  return {
    push,
    counts: () => ({ ...c }),
  };
}

/** The batch form: the same fold over a whole stream. */
export function foldInteraction(events: readonly InteractionInput[]): InteractionCounts {
  const fold = createInteractionFold();
  for (const ev of events) fold.push(ev);
  return fold.counts();
}

/** True when every counter is equal — the controller's "nothing new to say". */
export function equalCounts(
  a: Partial<InteractionCounts> | null | undefined,
  b: Partial<InteractionCounts> | null | undefined,
): boolean {
  const sa = sanitize(a);
  const sb = sanitize(b);
  return FIELDS.every((f) => sa[f] === sb[f]);
}

/** True when nothing has been counted yet. */
export function isZeroCounts(c: Partial<InteractionCounts> | null | undefined): boolean {
  return equalCounts(c, ZERO_COUNTS);
}
